import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiBook, FiCode, FiRotateCcw } from 'react-icons/fi';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { Tutorial, AvailableTutorial } from './types';
import PlaygroundEditor from './editor/PlaygroundEditor';
import { learnApi } from './api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/features/theme/contexts/ThemeContext';
import '@/styles/fonts.css';
import '../../styles/tutorial-content.css';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ValidationFeedback from './ValidationFeedback';
import CourseCompletionModal from './components/CourseCompletionModal';
import { Quiz } from './Quiz';

type ViewMode = 'tutorial' | 'split' | 'editor';

const TutorialPage = () => {
  const { t } = useTranslation();
  const { learningPathSlug, courseSlug, tutorialSlug } = useParams<{
    learningPathSlug: string;
    courseSlug: string;
    tutorialSlug: string;
  }>();

  // Log URL parameters
  useEffect(() => {
    console.log('URL Parameters:', {
      learningPathSlug,
      courseSlug,
      tutorialSlug,
      url: window.location.pathname
    });
  }, [learningPathSlug, courseSlug, tutorialSlug]);

  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const queryClient = useQueryClient();
  const [showValidation, setShowValidation] = useState(false);
  const [validationType, setValidationType] = useState<'success' | 'error'>('success');
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Redirect to reload page if session is uninitialized
  useEffect(() => {
    if (!authLoading && !session?.user?.id) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [session, authLoading]);

  // If no tutorialSlug is provided, redirect to the first tutorial
  useEffect(() => {
    const loadFirstTutorial = async () => {
      if ((!tutorialSlug || tutorialSlug === 'undefined') && learningPathSlug && courseSlug) {
        try {
          const tutorials = await learnApi.getTutorials(learningPathSlug, courseSlug);
          if (tutorials && tutorials.length > 0) {
            navigate(`/learn/${learningPathSlug}/${courseSlug}/${tutorials[0].id}`);
          } else {
            console.error('No tutorials found for course:', courseSlug);
          }
        } catch (error) {
          console.error('Error loading tutorials:', error);
        }
      }
    };
    loadFirstTutorial();
  }, [tutorialSlug, learningPathSlug, courseSlug, navigate]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-16">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (!session?.user?.id) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-16">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  // Fetch the current tutorial content
  const { data: tutorial, isLoading: isLoadingTutorial, error: tutorialError } = useQuery({
    queryKey: ['tutorial', learningPathSlug, courseSlug, tutorialSlug],
    queryFn: async () => {
      if (!learningPathSlug || !courseSlug) {
        const error = new Error(t('tutorial.missing_params'));
        console.error('Missing learning path or course:', {
          learningPathSlug,
          courseSlug,
          url: window.location.pathname,
          error: error.message
        });
        throw error;
      }

      if (!tutorialSlug || tutorialSlug === 'undefined') {
        return null;
      }

      try {
        console.log('Loading tutorial:', { learningPathSlug, courseSlug, tutorialSlug });
        const tutorials = await learnApi.getTutorials(learningPathSlug, courseSlug);
        if (!tutorials || tutorials.length === 0) {
          console.error('No tutorials found for course');
          throw new Error(t('tutorial.no_tutorials'));
        }
        console.log('Found tutorials:', tutorials.length);
        console.log('Tutorial IDs:', tutorials.map(t => ({ id: t.id })));

        const targetTutorial = tutorials.find(t => String(t.id) === tutorialSlug);

        if (!targetTutorial) {
          console.error('Tutorial not found:', { tutorialSlug, availableIds: tutorials.map(t => t.id) });
          throw new Error(t('tutorial.not_found'));
        }

        const tutorial = await learnApi.getTutorial(learningPathSlug, courseSlug, targetTutorial.id);

        if (!tutorial) {
          console.error('Failed to load tutorial content');
          throw new Error(t('tutorial.load_error'));
        }

        if (!tutorial.content?.markdown) {
          console.error('Tutorial has no content');
          throw new Error(t('tutorial.no_content'));
        }

        return tutorial;
      } catch (error) {
        console.error('Error loading tutorial:', error);
        throw error;
      }
    },
    enabled: !!session?.user?.id && !!learningPathSlug && !!courseSlug
  });

  // Fetch all tutorials for the course
  const { data: tutorials, isLoading: isLoadingTutorials } = useQuery({
    queryKey: ['tutorials', learningPathSlug, courseSlug],
    queryFn: () => learnApi.getTutorials(learningPathSlug, courseSlug),
    enabled: !!learningPathSlug && !!courseSlug,
  });

  // Fetch available (unlocked) tutorials based on progress
  const { data: availableTutorials } = useQuery<AvailableTutorial[]>({
    queryKey: ['availableTutorials', learningPathSlug, courseSlug, session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id || !courseSlug || !learningPathSlug) return [];
      return learnApi.getAvailableTutorials(learningPathSlug, courseSlug, session.user.id);
    },
    enabled: !!session?.user?.id && !!courseSlug && !!learningPathSlug,
  });

  // Responsive view 
  const [viewMode, setViewMode] = useState<ViewMode>('tutorial');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      const isDesktopView = window.innerWidth >= 1024;
      setIsDesktop(isDesktopView);
      if (isDesktopView && viewMode === 'editor') {
        setViewMode('split');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const playgroundEditorRef = useRef<any>(null);
  const handlePlaygroundRef = (ref: any) => {
    playgroundEditorRef.current = ref;
  };

  // Calculate current tutorial index and its unlocked status.
  const currentTutorialIndex = useMemo(() => {
    if (!tutorials) return -1;
    return tutorials.findIndex((tut) => String(tut.id) === tutorialSlug);
  }, [tutorials, tutorialSlug]);

  const isCurrentTutorialUnlocked = useMemo(() => {
    if (!tutorials || tutorials.length === 0) return true;
    if (currentTutorialIndex === 0) return true; // first tutorial always unlocked
    // For subsequent tutorials, check if the tutorial is available based on the availableTutorials data
    return availableTutorials?.find(t => String(t.id) === String(tutorials[currentTutorialIndex].id))?.isAvailable ?? false;
  }, [tutorials, availableTutorials, currentTutorialIndex]);

  const handleValidate = async () => {
    if (!tutorial || !courseSlug || !learningPathSlug || !session?.user?.id) return;

    try {
      setIsValidating(true);
      console.log('[Course Completion] Tutorial data:', {
        id: tutorial.id,
        title: tutorial.title,
        last_one: tutorial.last_one,
        courseSlug,
        learningPathSlug,
      });

      const success = await learnApi.updateTutorialProgress(
        learningPathSlug,
        courseSlug,
        tutorial.id,
        session.user.id
      );
      console.log('[Course Completion] Update progress result:', success);

      if (success) {
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey[0];
            return ['progress', 'availableTutorials', 'courseProgress', 'learningPathProgress'].includes(queryKey as string);
          },
        });

        setValidationType('success');
        setValidationMessages([t('tutorial.completion_success')]);
        setShowValidation(true);

        if (tutorial.last_one === true) {
          console.log('[Course Completion] Showing course completion modal');
          setShowCompletionModal(true);
        } else {
          console.log('[Course Completion] Navigating to next tutorial');
          setTimeout(() => {
            setShowValidation(false);
            handleValidationNext();
          }, 4000);
        }
      } else {
        setValidationType('error');
        setValidationMessages([t('tutorial.completion_error')]);
        setShowValidation(true);
      }
    } catch (error) {
      console.error('[Course Completion] Error during validation:', error);
      setValidationType('error');
      setValidationMessages([t('tutorial.completion_error')]);
      setShowValidation(true);
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidationClose = () => setShowValidation(false);

  const handleValidationNext = () => {
    if (!tutorials || isLoadingTutorials) return;
    const currentIndex = tutorials.findIndex((tut) => String(tut.id) === tutorialSlug);
    if (currentIndex >= 0 && currentIndex < tutorials.length - 1) {
      const nextTutorial = tutorials[currentIndex + 1];
      navigate(`/learn/${learningPathSlug}/${courseSlug}/${nextTutorial.id}`);
      setShowValidation(false);
    }
  };

  // Tutorial panel (markdown content)
  const TutorialPanel = (
    <div className="flex flex-col h-full min-h-0 relative bg-base-300 home-page">
      {/* Background circuit pattern with reduced opacity */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-2 pointer-events-none"></div>
      
      {/* Animated circuit lines with reduced intensity */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="circuit-line w-full h-px bg-info/10 absolute top-[5%] left-0 animate-pulse"></div>
        <div className="circuit-line w-px h-full bg-info/10 absolute top-0 left-[10%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
        <div className="circuit-line w-full h-px bg-info/10 absolute top-[95%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="circuit-line w-px h-full bg-info/10 absolute top-0 left-[90%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="sticky top-0 z-10 bg-base-100/95 border-b border-info/20 flex-shrink-0">
        <div className="flex items-center justify-between p-3 gap-2">
          <Link to="/learn" className="btn btn-ghost btn-xs sm:btn-sm group">
            <div className="relative">
              <FiArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-[-2px] transition-transform" />
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-info/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300"></span>
            </div>
            <span className="hidden sm:inline ml-2 font-tech-secondary">{t('learn.back')}</span>
          </Link>
          
          {tutorials && (
            <div className="relative">
              <select
                className="select select-bordered select-xs sm:select-sm max-w-[180px] sm:max-w-xs text-xs sm:text-sm font-tech-secondary bg-base-200/90 border-info/30 pl-4"
                value={tutorials.findIndex((tut) => String(tut.id) === tutorialSlug)}
                onChange={(e) => {
                  const index = parseInt(e.target.value);
                  const selectedTutorial = tutorials[index];
                  navigate(`/learn/${learningPathSlug}/${courseSlug}/${selectedTutorial.id}`);
                }}
              >
                {tutorials.map((tut, index) => {
                  // The first tutorial is always unlocked.
                  // For subsequent tutorials, check if the tutorial is available based on the availableTutorials data
                  const unlocked = index === 0 || (availableTutorials?.find(t => String(t.id) === String(tut.id))?.isAvailable ?? false);
                  return (
                    <option key={tut.id} disabled={!unlocked} value={index}>
                      {tut.order}. {tut.title} {!unlocked && '🔒'}
                    </option>
                  );
                })}
              </select>
              <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary"></div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-1 p-3 sm:p-6 overflow-y-auto min-h-0 relative">
        {isLoadingTutorial ? (
          <div className="flex justify-center items-center h-64">
            <div className="relative">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <div className="absolute inset-0 animate-pulse-glow opacity-50"></div>
            </div>
          </div>
        ) : tutorialError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="text-error text-center">
              <div className="glitch-container mb-4">
                <p className="font-bold text-lg font-tech-secondary" data-text={t('tutorial.load_error')}>
                  {t('tutorial.load_error')}
                </p>
              </div>
              <p className="text-sm opacity-75">{(tutorialError as Error).message}</p>
            </div>
            <button 
              className="btn btn-outline btn-error gap-2 mt-4 neon-glow" 
              onClick={() => queryClient.invalidateQueries({
                queryKey: ['tutorial', learningPathSlug, courseSlug, tutorialSlug]
              })}
            >
              <FiRotateCcw className="w-4 h-4" />
              {t('common.try_again')}
            </button>
          </div>
        ) : !tutorial?.content?.markdown ? (
          <div className="flex justify-center items-center h-64 text-error">
            <p className="font-tech-secondary">{t('tutorial.no_content')}</p>
          </div>
        ) : (
          <div className="flex flex-col min-h-full">
            <div className="bg-base-100/95 rounded-lg p-4 sm:p-6 border border-info/20 shadow-lg">
              <div 
                className={`tutorial-content flex-1 select-none ${isDark ? 'dark' : ''} prose prose-sm sm:prose dark:prose-invert max-w-none relative z-10`}
                onCopy={(e) => e.preventDefault()}
              >
                <div className="mb-4 pb-4 border-b border-primary/10">
                  <h1 className="text-xl sm:text-2xl font-bold font-tech text-primary mt-0 mb-2">
                    {tutorial.title}
                  </h1>
                  {tutorial.description && (
                    <p className="text-base-content/80 mt-0">{tutorial.description}</p>
                  )}
                </div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {tutorial.content.markdown}
                </ReactMarkdown>
              </div>
            </div>
            
            <div className="mt-auto pt-8 pb-4 flex justify-center">
              <button
                onClick={() => {
                  handleViewModeChange('editor');
                  if (!isDesktop) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="btn btn-primary gap-2 px-6 shadow-lg neon-glow group"
              >
                {tutorial?.has_quiz ? (
                  <>
                    <span className="font-tech-secondary">{t('tutorial.go_to_quiz')}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span className="font-tech-secondary">{t('tutorial.go_to_editor')}</span>
                    <FiCode className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Editor panel – if the current tutorial is locked, show a message instead
  const EditorPanel = (
    <div className="h-full min-h-0 flex flex-col relative">
      {/* Background circuit pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
      
      {!isCurrentTutorialUnlocked ? (
        <div className="flex flex-1 items-center justify-center relative">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="circuit-line w-full h-px bg-error/20 absolute top-[20%] left-0 animate-pulse"></div>
            <div className="circuit-line w-px h-full bg-error/20 absolute top-0 left-[25%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
            <div className="circuit-line w-full h-px bg-error/20 absolute bottom-[20%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="circuit-line w-px h-full bg-error/20 absolute top-0 right-[25%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
          </div>
          
          <div className="bg-base-100/80 backdrop-blur-sm p-6 rounded-lg border border-error/30 shadow-lg max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-error" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
            
            <div className="glitch-container mb-3">
              <h3 
                data-text={t('tutorial.locked_title', 'Access Locked')}
                className="text-xl font-bold text-error font-tech-secondary"
              >
                {t('tutorial.locked_title', 'Access Locked')}
              </h3>
            </div>
            
            <p className="text-base-content/70 mb-4">
              {t('tutorial.locked_message', 'This tutorial is locked. Please complete the previous lesson to unlock it.')}
            </p>
            
            <Link to="/learn" className="btn btn-outline btn-error btn-sm gap-2">
              <span>{t('tutorial.back_to_learn', 'Back to Learning Path')}</span>
              <FiArrowLeft className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : tutorial?.has_quiz && tutorial.content.quiz ? (
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="bg-base-100/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-primary/10 shadow-lg mb-4">
            <div className="flex items-center mb-4 gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-bold font-tech-secondary">{t('tutorial.quiz_title', 'Teste seu conhecimento!')}</h3>
            </div>
          </div>
          
          <Quiz 
            quizData={tutorial.content.quiz}
            onComplete={handleValidate}
            isDark={isDark}
          />
        </div>
      ) : (
        <PlaygroundEditor
          tutorial={
            tutorial ?? {
              id: '',
              title: '',
              slug: '',
              initial_code: { html: '', css: '', js: '', test: '' },
              content: { markdown: '' },
            }
          }
          ref={handlePlaygroundRef}
          onTestsPass={handleValidate}
        />
      )}
    </div>
  );

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    // Force a resize event after the view mode change
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Add extra bottom margin on mobile so that fixed bottom bar doesn't cover content */}
      <main className={`flex-1 flex flex-col overflow-hidden ${isDesktop ? '' : 'mb-16'}`}>
        {isDesktop ? (
          <div className="flex flex-row h-full">
            <div className={`${viewMode === 'editor' ? 'hidden' : viewMode === 'split' ? 'w-1/2' : 'w-full'} flex flex-col h-full overflow-hidden tutorial-content`}>
              {TutorialPanel}
            </div>
            <div className={`${viewMode === 'tutorial' ? 'hidden' : viewMode === 'split' ? 'w-1/2' : 'w-full'} flex flex-col h-full overflow-hidden playground-editor`}>
              {EditorPanel}
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {viewMode === 'split' ? (
              <>
                <div className="flex-1 flex flex-col h-1/2 overflow-hidden tutorial-content">
                  {TutorialPanel}
                </div>
                <div className="flex-1 flex flex-col h-1/2 overflow-hidden playground-editor">
                  {EditorPanel}
                </div>
              </>
            ) : (
              <>
                <div className={`${viewMode === 'tutorial' ? 'flex' : 'hidden'} flex-col h-full overflow-hidden tutorial-content`}>
                  {TutorialPanel}
                </div>
                <div className={`${viewMode === 'editor' ? 'flex' : 'hidden'} flex-col h-full overflow-hidden playground-editor`}>
                  {EditorPanel}
                </div>
              </>
            )}
          </div>
        )}
      </main>
      <br></br>
      {/* Bottom navigation bar – fixed on mobile */}
      <div className={`${isDesktop ? '' : 'fixed bottom-0 left-0 right-0 z-50'} bg-base-100/90 backdrop-blur-md border-t border-primary/20 shrink-0 shadow-lg`}>
        <div className="flex justify-between items-center p-2 max-w-screen-xl mx-auto gap-2">
          <div className="flex items-center gap-2">
            <button
              className={`group relative transition-all duration-300 ${viewMode === 'tutorial' 
                ? 'bg-primary/10 text-primary' 
                : 'hover:bg-base-200/70'} rounded-lg p-2`}
              onClick={() => handleViewModeChange('tutorial')}
              title={t('learn.tutorial')}
            >
              <FiBook className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-primary/90 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t('learn.tutorial')}
              </span>
            </button>
            
            <button
              className={`group relative transition-all duration-300 ${viewMode === 'split' 
                ? 'bg-primary/10 text-primary' 
                : 'hover:bg-base-200/70'} rounded-lg p-2`}
              onClick={() => handleViewModeChange('split')}
              title={t('learn.split')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="12" y1="3" x2="12" y2="21" />
              </svg>
              <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-primary/90 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t('learn.split')}
              </span>
            </button>
            
            <button
              className={`group relative transition-all duration-300 ${viewMode === 'editor' 
                ? 'bg-primary/10 text-primary' 
                : 'hover:bg-base-200/70'} rounded-lg p-2`}
              onClick={() => handleViewModeChange('editor')}
              title={t('learn.editor')}
            >
              <FiCode className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-primary/90 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t('learn.editor')}
              </span>
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => tutorial && playgroundEditorRef.current?.updatePlaygroundContent(tutorial)}
              className={`group relative hover:bg-base-200/70 rounded-lg p-2 transition-all duration-300 ${isLoadingTutorial ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isLoadingTutorial}
              title={t('learn.editor.reset')}
            >
              <FiRotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-base-300 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t('learn.editor.reset')}
              </span>
            </button>
            
            <button
              onClick={handleValidate}
              className={`btn btn-primary btn-sm sm:btn-md gap-2 min-w-[50px] sm:min-w-[120px] shadow-lg ${!isLoadingTutorial && !isValidating && isCurrentTutorialUnlocked && !tutorial?.has_quiz ? 'neon-glow' : ''}`}
              disabled={isLoadingTutorial || isValidating || !isCurrentTutorialUnlocked || tutorial?.has_quiz}
              title={tutorial?.has_quiz ? t('learn.validation.complete_quiz_first') : ''}
            >
              {isValidating ? (
                <span className="loading loading-spinner loading-xs sm:loading-sm"></span>
              ) : (
                <>
                  <span className="hidden sm:inline font-tech-secondary">
                    {tutorial?.has_quiz 
                      ? t('learn.validation.complete_quiz') 
                      : t('learn.validation.complete')}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Animated circuit line */}
        <div className="absolute -top-px left-0 w-full h-px bg-primary/30 overflow-hidden">
          <div className="w-20 h-full bg-primary/70 animate-[moveLeftRight_3s_linear_infinite]"></div>
        </div>
      </div>
      {showValidation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={handleValidationClose} />
          <ValidationFeedback
            messages={validationMessages}
            type={validationType}
            onClose={handleValidationClose}
            onNext={handleValidationNext}
            hasNext={
              tutorials &&
              tutorials.findIndex((tut) => String(tut.id) === tutorialSlug) < tutorials.length - 1
            }
            autoProgressDelay={30000}
          />
        </div>
      )}
      {showCompletionModal && (
        <CourseCompletionModal
          isOpen={showCompletionModal}
          onClose={() => {
            console.log('[Course Completion] Closing modal and navigating to course page');
            setShowCompletionModal(false);
            navigate(`/learn/${courseSlug}`);
          }}
          courseId={courseSlug || ''}
          pathId={learningPathSlug || ''}
        />
      )}
    </div>
  );
};

export default TutorialPage;